import * as React from "react";
import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions } from "@tiptap/suggestion";
import type { Instance as TippyInstance } from "tippy.js";
import { createCollaborateSuggestionTippy } from "./collaborateSuggestionTippy";
import CollaborateMentionList, {
  type CollaborateMentionListRef,
} from "../components/editor/CollaborateMentionList";
import type { CollaborateUser } from "../types/collaborate";
import { filterCollaborateUsers, userToMentionItem } from "./collaborateMentions";
import { hasCommandBadge } from "./collaboratePlaybookChips";

export function createCollaborateMentionSuggestion(
  getUsers: () => CollaborateUser[]
): Omit<SuggestionOptions, "editor"> {
  return {
    char: "@",
    allowSpaces: false,
    allow: ({ editor }) => !hasCommandBadge(editor),
    items: ({ query }) =>
      filterCollaborateUsers(getUsers(), query)
        .slice(0, 8)
        .map(userToMentionItem),
    render: () => {
      let component: ReactRenderer<CollaborateMentionListRef> | null = null;
      let popup: TippyInstance[] | null = null;

      return {
        onStart: (props) => {
          component = new ReactRenderer(CollaborateMentionList, {
            props: {
              items: props.items,
              command: props.command,
            },
            editor: props.editor,
          });

          if (!props.clientRect) {
            return;
          }

          popup = createCollaborateSuggestionTippy({
            getReferenceClientRect: props.clientRect as () => DOMRect,
            content: component.element,
          });
        },

        onUpdate(props) {
          component?.updateProps({
            items: props.items,
            command: props.command,
          });
          if (popup?.[0] && props.clientRect) {
            popup[0].setProps({
              getReferenceClientRect: props.clientRect as () => DOMRect,
            });
          }
        },

        onKeyDown(props) {
          if (props.event.key === "Escape") {
            popup?.[0]?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },

        onExit() {
          popup?.[0]?.destroy();
          component?.destroy();
          popup = null;
          component = null;
        },
      };
    },
  };
}
